import { Dispatch, SetStateAction } from 'react';

import { EditStrategyPageInvestmentQuery, Security_Status } from '@/__generated__/graphql';
import { ISelectValue } from '@/components/basicComponents/select';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { IOwner } from '@/components/dealCatalog-page/types';
import { getUTCDate } from '@/shared/utils/date';
import { InvestmentPacingType } from '@trace-8/trace8-schema';
import { SectionDataItem } from '.';
import { Tenant } from '../types';
import { distributionSettingsOptions } from './constants';
import { IListData, IListDataWithId, IModifyStrategySectionData } from './types';

export const getOwnerValues = (ownerData: Array<IOwner>) => {
  return ownerData?.map((owner) => `${owner.firstName} ${owner.lastName}`) || [];
};

const getOwnerName = (ownerData: EditStrategyPageInvestmentQuery['Investment']['investmentOwner'] | undefined) => {
  return ownerData ? `${ownerData?.firstName} ${ownerData?.lastName}` : '';
};

export const getOwnerEmailFromName = (ownerName: string, owners: Array<IOwner>) => {
  const owner = owners.filter((owner: IOwner) => ownerName === getOwnerName(owner));
  return owner[0]?.email || null;
};

interface IGetAllPossibleOwners {
  investmentOwners: IOwner[];
  operationsOwners: IOwner[];
  complianceOwners: IOwner[];
}

export const getAllPossibleOwners = ({ investmentOwners, operationsOwners, complianceOwners }: IGetAllPossibleOwners): IOwner[] => {
  return [...investmentOwners, ...operationsOwners, ...complianceOwners].filter(Boolean);
};

export const getDynamicSelectPreviewValue = (value: string, list: ISelectValue[]) => {
  const item = list.find((item) => item.value === value);
  return item?.label || '';
};

export const addDynamicSelect = (setData: Dispatch<SetStateAction<IListDataWithId>>) => {
  setData((prevData) => ({ ...prevData, selected: [...prevData.selected, ''] }));
};

export const onChangeDynamicSelect = (value: string, index: number, data: IListDataWithId, setData: Dispatch<SetStateAction<IListDataWithId>>) => {
  const newSeletedValues = [...data.selected];
  newSeletedValues[index] = value;
  const filteredList = data.list.filter((item) => item.value !== value);
  const removedSelectOption = data.allList.find((item) => item.value === data.selected[index]);

  if (removedSelectOption) {
    setData((prevData) => ({
      ...prevData,
      selected: newSeletedValues,
      list: [removedSelectOption, ...filteredList]
    }));
    return;
  }
  setData((prevData) => ({ ...prevData, selected: newSeletedValues, list: filteredList }));
};

export const deleteDynamicSelect = (index: number, data: IListDataWithId, setData: Dispatch<SetStateAction<IListDataWithId>>) => {
  const newSeletedValues = [...data.selected];
  newSeletedValues.splice(index, 1);
  const removedSelectOption = data.allList.find((item) => item.value === data.selected[index]);

  setData((prevData) => ({
    ...prevData,
    selected: newSeletedValues,
    list: removedSelectOption ? [...prevData.list, removedSelectOption] : prevData.list
  }));
};

export const addStrategies = (data: IListData, setData: Dispatch<SetStateAction<IListData>>) => {
  setData((prevData) => ({ ...prevData, chosen: [...prevData.chosen, ''] }));
};

export const onChangeStrategies = (value: string, index: number, data: IListData, setData: Dispatch<SetStateAction<IListData>>) => {
  const newChosenValue = [...data.chosen];
  newChosenValue[index] = value;
  const filteredList = data.list.filter((item) => item !== value);

  if (data.chosen[index] !== '') {
    setData((prevData) => ({
      ...prevData,
      chosen: newChosenValue,
      list: [data.chosen[index], ...filteredList]
    }));
    return;
  }
  setData((prevData) => ({ ...prevData, chosen: newChosenValue, list: filteredList }));
};

export const delStrategies = (el: string, index: number, data: IListData, setData: Dispatch<SetStateAction<IListData>>) => {
  const newChosenValue = [...data.chosen];
  newChosenValue.splice(index, 1);
  setData((prevData) => ({ ...prevData, chosen: newChosenValue, list: el !== '' ? [...prevData.list, el] : prevData.list }));
};

export const extractDirtyFields = <T extends Record<string, any>>(data: T, dirtyFields: Partial<Record<keyof T, boolean>>): Partial<T> => {
  return Object.keys(dirtyFields).reduce((acc, key) => {
    acc[key as keyof T] = data[key as keyof T];
    return acc;
  }, {} as Partial<T>);
};

export const getInvestmentRestrictionsData = (
  investmentId: string,
  selectedDistributionSettingsOptions: string,
  allAdvisoryFirmList: Tenant[],
  advisoryFirms: IListData
) => {
  if (selectedDistributionSettingsOptions === distributionSettingsOptions[0] || !advisoryFirms.chosen.length || advisoryFirms.chosen[0] === 'None') {
    const tenantIds = allAdvisoryFirmList?.map((advisoryFirm) => advisoryFirm.id);
    if (!tenantIds?.length) return;
    return { id: investmentId, tenantIds, showToAllAdvisoryFirms: true };
  }
  const tenantIds = advisoryFirms.chosen
    .map((advisoryFirmName) => {
      const tenant = allAdvisoryFirmList?.find((advisoryFirm) => advisoryFirm.name === advisoryFirmName);

      if (tenant) {
        return tenant.id;
      }
      return null;
    })
    .filter((tenantId) => tenantId);

  return { id: investmentId, tenantIds, showToAllAdvisoryFirms: false };
};

export const getSectionsData = (investment: EditStrategyPageInvestmentQuery['Investment']): IModifyStrategySectionData[] => {
  const {
    name,
    assetClass,
    subAssetClass,
    pacingType,
    pacingStartingYear,
    pacingStartingQuarter,
    ubtiBlocker,
    custodied,
    investorDocsDue,
    nextClose,
    finalClose,
    investmentOwner,
    operationsOwner,
    complianceOwner,
    minimum,
    type,
    subAssetClasses,
    tags,
    advisoryFirms,
    showToAllAdvisoryFirms
  } = investment || {};

  return [
    {
      title: 'Basic Info',
      name: 'basicInfo',
      formData: {
        name,
        assetClassId: assetClass?.id ?? '',
        subAssetClassId: subAssetClass?.id ?? '',
        pacingType: InvestmentPacingType[pacingType] || null,
        pacingStartingYear,
        pacingStartingQuarter,
        ubtiBlocker,
        custodied
      },
      data: [
        { title: 'Name', value: name },
        { title: 'Asset Class', value: assetClass?.name ?? null },
        { title: 'Sub Asset Class', value: subAssetClass?.name ?? null },
        { title: 'Pacing Type', value: InvestmentPacingType[pacingType] },
        { title: 'Pacing Starting Year', value: pacingStartingYear },
        { title: 'Pacing Starting Quarter', value: pacingStartingQuarter },
        {
          title: 'Sectors',
          value: subAssetClasses?.length ? (
            <div>
              {subAssetClasses?.map((item, index) => (
                <SectionDataItem key={`sectors-${index}`} isBold>
                  {item}
                </SectionDataItem>
              ))}
            </div>
          ) : null
        },
        {
          title: 'Tags',
          value: tags?.length ? (
            <div>
              {tags?.map((tag, index) => (
                <SectionDataItem key={`tags-${index}`} isBold>
                  {tag}
                </SectionDataItem>
              ))}
            </div>
          ) : null
        }
      ]
    },
    {
      title: 'Allocation Settings',
      name: 'allocationSettings',
      formData: {
        minimum: minimum?.toString() ?? '',
        type,
        ubtiBlocker,
        custodied
      },
      data: [
        {
          title: 'Minimum Investor Commitment',
          value: typeof minimum === 'number' ? <FormattingTooltip zIndex={1000}>{minimum}</FormattingTooltip> : null
        },
        { title: 'Type', value: type },
        { title: 'Tax Exempt Suitable', value: typeof ubtiBlocker === 'boolean' ? (ubtiBlocker ? 'Yes' : 'No') : null },
        { title: 'Custodied', value: typeof custodied === 'boolean' ? (custodied ? 'Yes' : 'No') : null },
        { title: 'Restrictions', value: showToAllAdvisoryFirms ? 'Show to all advisory firms' : 'Selected advisory firms only' },
        {
          title: 'Show to',
          value: showToAllAdvisoryFirms ? (
            'All Advisory Firms'
          ) : advisoryFirms?.length ? (
            <div>
              {advisoryFirms?.map((item) => (
                <SectionDataItem key={item.id} isBold>
                  {item.name}
                </SectionDataItem>
              ))}
            </div>
          ) : null
        }
      ]
    },
    {
      title: 'Closings',
      name: 'closings',
      formData: {
        investorDocsDue,
        nextClose,
        finalClose
      },
      data: [
        { title: 'Investor Docs Due', value: investorDocsDue ? getUTCDate(new Date(investorDocsDue)) : null },
        { title: 'Next Close', value: nextClose ? getUTCDate(new Date(nextClose)) : null },
        { title: 'Final Close', value: finalClose ? getUTCDate(new Date(finalClose)) : null }
      ]
    },
    {
      title: 'Vehicles',
      name: 'vehicles',
      formData: {},
      data: []
    },
    {
      title: 'Owners',
      name: 'owners',
      formData: {
        investmentOwner: getOwnerName(investmentOwner),
        operationsOwner: getOwnerName(operationsOwner),
        complianceOwner: getOwnerName(complianceOwner)
      },
      data: [
        { title: 'Investment Owner', value: getOwnerName(investmentOwner) },
        { title: 'Operations Owner', value: getOwnerName(operationsOwner) },
        { title: 'Compliance Owner', value: getOwnerName(complianceOwner) }
      ]
    }
  ];
};

const requiredFieldsForSeekingInterestAndOpen = [
  'name',
  'assetClassId',
  'subAssetClassId',
  'pacingType',
  'pacingStartingYear',
  'pacingStartingQuarter',
  'ubtiBlocker',
  'minimum',
  'type',
  'custodied'
];

const requiredFields = {
  [Security_Status.Draft]: ['name'],
  [Security_Status.Lead]: ['name'],
  [Security_Status.Declined]: ['name'],
  [Security_Status.DueDiligence]: ['name'],
  [Security_Status.Rejected]: ['name'],
  [Security_Status.Approved]: ['name'],
  [Security_Status.SeekingInterest]: requiredFieldsForSeekingInterestAndOpen,
  [Security_Status.Open]: requiredFieldsForSeekingInterestAndOpen,
  [Security_Status.Active]: ['name', 'assetClassId', 'subAssetClassId', 'pacingType', 'pacingStartingYear', 'pacingStartingQuarter'],
  [Security_Status.Redeemed]: ['name'],
  [Security_Status.Realized]: ['name'],
  [Security_Status.Terminated]: ['name']
};

export const isFieldRequiredByStatus = (fieldName: string, status: Security_Status): boolean => {
  return requiredFields[status as keyof typeof requiredFields]?.includes(fieldName) || false;
};

export const sortUniqueNoneEmpty = (arr: ISelectValue[]) =>
  arr.filter((value, index, array) => array.indexOf(value) === index && value.value !== '').sort((a, b) => a.value.localeCompare(b.value));
